import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#setupPhotoSwipe();
  }

  #controller = new AbortController();
  #photoSwipeLightbox;
  #photoSwipePromise;
  #photoSwipeItems = [];
  #photoSwipeSlidesRoot;
  #pointerDownAt;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#photoSwipeLightbox?.destroy();
    this.#photoSwipeLightbox = undefined;
    this.#photoSwipePromise = undefined;
    this.#photoSwipeSlidesRoot = undefined;
    this.#pointerDownAt = undefined;
  }

  #setupPhotoSwipe() {
    const itemsElement = this.querySelector('[data-product-gallery-photoswipe-items]');
    const slidesRoot = this.querySelector('slideshow-slides');
    const lightboxUrl = this.dataset.photoswipeLightboxUrl;
    const moduleUrl = this.dataset.photoswipeModuleUrl;

    if (!(itemsElement instanceof HTMLScriptElement) || !(slidesRoot instanceof HTMLElement)) return;
    if (!lightboxUrl || !moduleUrl) return;

    try {
      this.#photoSwipeItems = JSON.parse(itemsElement.textContent || '[]');
    } catch (error) {
      console.error('[Product gallery] Unable to read PhotoSwipe images.', error);
      return;
    }

    if (this.#photoSwipeItems.length === 0) return;

    this.#photoSwipeSlidesRoot = slidesRoot;
    this.#disableThemeZoomOnImageSlides();

    const { signal } = this.#controller;
    slidesRoot.addEventListener('pointerdown', this.#handlePhotoSwipePointerDown, { passive: true, signal });
    slidesRoot.addEventListener('click', this.#handlePhotoSwipeClick, { capture: true, signal });
  }

  #handlePhotoSwipePointerDown = (event) => {
    const slide = this.#getPhotoSwipeSlide(event);
    if (!slide) {
      this.#pointerDownAt = undefined;
      return;
    }

    this.#pointerDownAt = {
      time: performance.now(),
      x: event.clientX,
      y: event.clientY,
    };
  };

  #handlePhotoSwipeClick = (event) => {
    const slide = this.#getPhotoSwipeSlide(event);
    if (!slide) return;

    if (this.#pointerDownAt) {
      const elapsed = performance.now() - this.#pointerDownAt.time;
      const horizontalMovement = Math.abs((event.clientX || 0) - this.#pointerDownAt.x);
      const verticalMovement = Math.abs((event.clientY || 0) - this.#pointerDownAt.y);
      this.#pointerDownAt = undefined;

      if (elapsed > 600 || horizontalMovement > 6 || verticalMovement > 6) return;
    }

    const imageIndex = this.#getPhotoSwipeImageSlides().indexOf(slide);
    if (imageIndex < 0 || !this.#photoSwipeItems[imageIndex]) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    void this.#openPhotoSwipe(imageIndex, slide, event);
  };

  #getPhotoSwipeSlide(event) {
    const slide = event.composedPath().find((element) => element instanceof HTMLElement && element.tagName === 'SLIDESHOW-SLIDE');

    if (!(slide instanceof HTMLElement)) return undefined;
    if (slide.getAttribute('aria-hidden') !== 'false') return undefined;
    if (!slide.classList.contains('product-media-container--image')) return undefined;

    return slide;
  }

  #getPhotoSwipeImageSlides() {
    if (!this.#photoSwipeSlidesRoot) return [];

    return Array.from(
      this.#photoSwipeSlidesRoot.querySelectorAll('slideshow-slide.product-media-container--image')
    );
  }

  #disableThemeZoomOnImageSlides() {
    for (const slide of this.#getPhotoSwipeImageSlides()) {
      slide.removeAttribute('on:click');
      for (const zoomButton of slide.querySelectorAll('.product-media-container__zoom-button')) {
        zoomButton.remove();
      }
    }
  }

  async #openPhotoSwipe(imageIndex, slide, event) {
    try {
      const lightbox = await this.#getPhotoSwipe();
      lightbox.options.dataSource = this.#photoSwipeItems;
      lightbox.loadAndOpen(imageIndex);
    } catch (error) {
      console.error('[Product gallery] Unable to open PhotoSwipe.', error);

      const mediaIndex = this.#photoSwipeSlidesRoot
        ? Array.from(this.#photoSwipeSlidesRoot.querySelectorAll('slideshow-slide')).indexOf(slide)
        : -1;
      if (mediaIndex >= 0 && this.isConnected) this.zoom(mediaIndex, event);
    }
  }

  #getPhotoSwipe() {
    if (this.#photoSwipeLightbox) return Promise.resolve(this.#photoSwipeLightbox);
    if (this.#photoSwipePromise) return this.#photoSwipePromise;

    const lightboxUrl = this.dataset.photoswipeLightboxUrl;
    const moduleUrl = this.dataset.photoswipeModuleUrl;
    if (!lightboxUrl || !moduleUrl) return Promise.reject(new Error('PhotoSwipe asset URLs are missing.'));

    this.#photoSwipePromise = import(lightboxUrl)
      .then(({ default: PhotoSwipeLightbox }) => {
        const lightbox = new PhotoSwipeLightbox({
          dataSource: this.#photoSwipeItems,
          pswpModule: () => import(moduleUrl),
          bgOpacity: 0.92,
        });
        lightbox.init();

        if (!this.isConnected) {
          lightbox.destroy();
          throw new Error('The product gallery was replaced before PhotoSwipe loaded.');
        }

        this.#photoSwipeLightbox = lightbox;
        return lightbox;
      })
      .catch((error) => {
        this.#photoSwipePromise = undefined;
        throw error;
      });

    return this.#photoSwipePromise;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
